import type { CaptureContext, SnapdomPlugin } from '@zumer/snapdom';

const CSS_TOKEN_REF = /var\(\s*(--[\w-]+)/g;

function collectReferencedTokens(el: Element, names: Set<string>) {
  for (let index = 0; index < el.attributes.length; index++) {
    const attribute = el.attributes[index];
    for (const match of attribute.value.matchAll(CSS_TOKEN_REF)) {
      names.add(match[1]);
    }
  }

  for (let index = 0; index < el.children.length; index++) {
    collectReferencedTokens(el.children[index], names);
  }
}

/**
 * The snapshot is rendered as a standalone SVG in an isolated document, so the `:root`
 * design tokens are gone by the time it rasterizes. Most paint survives anyway because
 * snapdom writes each rendered element's *computed* style (with `var()` already
 * substituted) into the snapshot. Elements inside `<defs>` are the exception — snapdom
 * copies a gradient's `<stop stop-color="var(--token)">` across verbatim, and with the
 * token undefined it falls back to the initial value, painting chart area fills black.
 *
 * Rather than special-casing `<stop>`, redeclare every token the subtree references on
 * the clone root so the existing `var()` references simply resolve. That also covers
 * masks, filters and patterns, which fail the same way.
 */
export const pinReferencedCssTokens: SnapdomPlugin = {
  name: 'pin-referenced-css-tokens',
  afterClone(context: CaptureContext) {
    const { clone, element } = context;
    if (!clone) return;

    const names = new Set<string>();
    collectReferencedTokens(clone, names);
    if (!names.size) return;

    // resolve against the captured element, not `:root` — a theme wrapper in between
    // can re-scope a token, and the capture should match what the user is looking at.
    const scope = getComputedStyle(element);
    for (const name of names) {
      const value = scope.getPropertyValue(name);
      if (value) clone.style.setProperty(name, value);
    }
  },
};
